import { gameDataService } from './GameDataService';
import { BodyLocation, type Armor } from '../types/character';

export interface ResolvedArmorStats {
  armorPoints: number;
  locations: BodyLocation[];
  special: string[];
  /** False when the values came from the character's stored copy, not the catalogue. */
  fromCatalogue: boolean;
}

/**
 * Resolve an armour piece's protective stats, deriving them from the item
 * catalogue rather than trusting the copy stored on the character.
 *
 * Why this exists
 * ---------------
 * `Armor` on the character carries `armorPoints`, `locations` and `special` as a
 * COPY of the catalogue values, taken when the item was acquired. Those fields
 * are pure catalogue data - they never vary per instance - so the copy can only
 * ever go stale. It did: four Faith and Coin armour entries shipped locations of
 * "arms"/"legs", and any character who had already bought one kept the bad value.
 *
 * A load-time migration was tried first and removed in favour of this. Copying
 * then reconciling means two sources of truth and a repair step that has to run
 * on every load; deriving means there is nothing to drift.
 *
 * The stored copy is still the fallback, deliberately: a homebrew piece or an
 * item since removed from the catalogue has no entry to derive from, and its
 * character should not silently lose protection.
 *
 * Instance-specific state - `equipped`, `craftsmanship`, notes, modifications -
 * stays on the character where it belongs and is NOT touched here. Craftsmanship
 * and Reinforced AP bonuses are layered on downstream by CraftsmanshipService and
 * calculateTotalArmor respectively.
 */
export function resolveArmorStats(piece: Armor): ResolvedArmorStats {
  const catalogueArmor = gameDataService.getArmorByStringId(piece.sourceId);

  if (catalogueArmor) {
    return {
      armorPoints: catalogueArmor.armorPoints,
      locations: catalogueArmor.locations as BodyLocation[],
      special: catalogueArmor.special ?? [],
      fromCatalogue: true
    };
  }

  return {
    armorPoints: piece.armorPoints,
    locations: piece.locations,
    special: piece.special ?? [],
    fromCatalogue: false
  };
}
